import dayjs from 'dayjs'

const CSV_HEADERS = [
  'employeeUid', 'firstName', 'lastName',
  'op_assessmentOfPotential', 'op_skillDevelopmentNeeds', 'op_nominatedFor', 'op_talentPoolLevel', 'op_isFlightRisk', 'op_talentReviewComments', 'op_managersComments', 'op_managerOnceRemovedComments',
  'disc_assessmentOfPotential', 'disc_ultimatePotential', 'disc_mpaPotential', 'disc_promotability', 'disc_nextMove', 'disc_action', 'disc_reviewComments',
  'talentOperationalMovements', 'careerActionsDoc'
]

const parseBool = (value) => String(value || '').toLowerCase() === 'true'
const parseList = (value) => value ? value.split('|').map(item => item.trim()).filter(Boolean) : []

const parseCsvLine = (line) => {
  const parsed = []
  let current = ''
  let inQuotes = false
  for (let i = 0; i < line.length; i++) {
    const char = line[i]
    if (char === '"' && line[i + 1] === '"') {
      current += '"'
      i += 1
      continue
    }
    if (char === '"') {
      inQuotes = !inQuotes
      continue
    }
    if (char === ',' && !inQuotes) {
      parsed.push(current)
      current = ''
      continue
    }
    current += char
  }
  parsed.push(current)
  return parsed.map(x => x.replace(/\r/g, '').trim())
}

const csvEscape = (value) => {
  const clean = String(value ?? '')
  if (clean.includes(',') || clean.includes('"') || clean.includes('\n')) {
    return `"${clean.replace(/"/g, '""')}"`
  }
  return clean
}

export const buildTemplate = (selectedEmployees) => {
  const lines = [CSV_HEADERS.join(',')]
  selectedEmployees.forEach((employee) => {
    const values = CSV_HEADERS.map((header) => {
      if (header === 'employeeUid') return employee.uid
      if (header === 'firstName') return employee.firstName || ''
      if (header === 'lastName') return employee.lastName || ''
      return ''
    })
    lines.push(values.map(csvEscape).join(','))
  })
  return lines.join('\n')
}

export const parseCsv = (text) => {
  const lines = text.split('\n').map(line => line.trim()).filter(Boolean)
  if (!lines.length) return []
  const headers = parseCsvLine(lines[0])
  const data = []
  for (let i = 1; i < lines.length; i++) {
    const values = parseCsvLine(lines[i])
    const row = {}
    headers.forEach((header, idx) => {
      row[header] = values[idx] || ''
    })
    if (Object.values(row).join('').trim()) {
      data.push(row)
    }
  }
  return data
}

export const rowFromCsv = (csvRow, employee) => {
  const movementRaw = (csvRow.talentOperationalMovements || '').split(';').map(x => x.trim()).filter(Boolean)
  const actionRaw = (csvRow.careerActionsDoc || '').split(';').map(x => x.trim()).filter(Boolean)

  return {
    employeeUid: employee.uid,
    firstName: employee.firstName,
    lastName: employee.lastName,
    talentOperationalReview: {
      assessmentOfPotential: csvRow.op_assessmentOfPotential || '',
      skillDevelopmentNeeds: parseList(csvRow.op_skillDevelopmentNeeds),
      nominatedFor: csvRow.op_nominatedFor || '',
      talentPoolLevel: csvRow.op_talentPoolLevel || '',
      isFlightRisk: parseBool(csvRow.op_isFlightRisk),
      talentReviewComments: csvRow.op_talentReviewComments || '',
      managersComments: csvRow.op_managersComments || '',
      managerOnceRemovedComments: csvRow.op_managerOnceRemovedComments || '',
      careerActionsDoc: actionRaw.map((action) => {
        const [title = '', date = '', responsibility = ''] = action.split('|')
        return {
          action: title.trim(),
          date: date ? dayjs(date.trim()) : null,
          responsibility: responsibility.trim()
        }
      })
    },
    talentDisciplineReview: {
      assessmentOfPotential: csvRow.disc_assessmentOfPotential || '',
      ultimatePotential: csvRow.disc_ultimatePotential || '',
      mpaPotential: csvRow.disc_mpaPotential || '',
      promotability: csvRow.disc_promotability || '',
      nextMove: csvRow.disc_nextMove || '',
      action: csvRow.disc_action || '',
      reviewComments: csvRow.disc_reviewComments || ''
    },
    talentOperationalMovements: movementRaw.map((movement) => {
      const [movementType = '', time = '', nextMove = ''] = movement.split('|')
      return {
        movementType: movementType.trim(),
        time: time.trim(),
        nextMove: nextMove.trim()
      }
    }),
    talentOperationalMovementsToDelete: [],
    status: 'pending',
    errorMessage: '',
    dirtyAfterSuccess: false
  }
}

export const toCsvMovements = (movements = []) => {
  return movements
    .map(movement => [movement?.movementType || '', movement?.time || '', movement?.nextMove || ''].join('|'))
    .join(';')
}

export const toCsvActions = (actions = []) => {
  return actions
    .map(action => {
      const rawDate = action?.date
      const formattedDate = rawDate
        ? (dayjs.isDayjs(rawDate) ? rawDate.format('YYYY-MM-DD') : dayjs(rawDate).format('YYYY-MM-DD'))
        : ''
      return [action?.action || '', formattedDate, action?.responsibility || ''].join('|')
    })
    .join(';')
}

